import type {
  Carer,
  HomeDto,
  Resident,
  ResidentBody,
  ResidentCarePackageDTO
} from '@lmc/ts-common'
import {
  Add,
  Button,
  Form,
  FormCard,
  FormItem,
  PageHeader,
  TextArea
} from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import { canManagePayAndInvoicing } from 'common/permissions'
import { getNominative, showToast } from 'common/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import useResident from 'hooks/useResident'
import { useRouter } from 'hooks/useRouter'
import {
  type LegacyRef,
  type RefObject,
  useEffect,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { scrollToSection } from 'screens/Residents/screens/ResidentProfileScreen/utils/utils'
import { commonPatterns } from 'utils'
import DeleteFundingTypeModal from '../ChangeManagement/DeleteFundingTypeModal'
import { ButtonsContainer, PageWrapper, SectionHeader } from '../components'
import { handleOnFinishFailed } from '../formComponents/utils'
import type { ChargeRateFormEntry } from '../hooks/useChargeRates'
import {
  useGetChargeRates,
  useUpdateChargeRates
} from '../hooks/useChargeRates'
import useCheckFundingType, {
  type FundingTypeUsageCheck
} from '../hooks/useCheckFundingType'
import { useGetInvoicingSettings } from '../hooks/useInvoicingRates'
import { useVisitTypesList } from '../hooks/useVisitTypesList'
import { FundingFormItem } from './components/FundingFormItem'

type FundingSourceWithChargeRates = {
  type: string
  hoursPerWeek?: number
  chargeRates?: ChargeRateFormEntry[]
}

export const EditCarePackageAndPatternsScreen = () => {
  const { t } = useTranslation()
  const sendEvent = useMixpanelEvent()
  const [form] = AntdForm.useForm()
  const { navigate, params } = useRouter()
  const [isDirty, setIsDirty] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const { resident, updateResident, isLoadingUpdate } = useResident({
    id: params?.id,
    onUpdateSuccess: () => setHasSaved(true)
  })
  const flags = useFeatureFlags({
    flagNames: ['rostering_change_management_part_2', 'rota_invoicing']
  })?.data
  const { hasRota } = useHomeType()

  const ref = useRef()

  const flagsRosteringChangeManagementPart2Enabled =
    flags?.rostering_change_management_part_2?.enabled

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )
  const canManageChargeRates = canManagePayAndInvoicing(userRole)

  const { data: invoicingSettings } = useGetInvoicingSettings()
  const { visitTypes } = useVisitTypesList()
  const showChargeRates =
    flags?.rota_invoicing?.enabled &&
    invoicingSettings?.enabled &&
    canManageChargeRates

  const { data: chargeRatesData } = useGetChargeRates(
    showChargeRates ? resident?.id : undefined
  )
  const { updateChargeRates } = useUpdateChargeRates(resident?.id)
  const chargeRatesInitialized = useRef(false)

  const residentNominative = getNominative(
    home.preferredPeopleNominative,
    'Resident'
  )

  const fundingTypeOptions: DefaultOptionType[] =
    home.rotaSettings?.fundingTypes?.map((fundingType) => ({
      label: fundingType.name,
      value: fundingType.id
    })) || []

  const residentWithPackages = resident as unknown as Resident

  const [showDeleteFundingTypeModal, setShowDeleteFundingTypeModal] =
    useState(false)

  const { checkFundingTypeUsedInRota, isLoading: isCheckingFundingTypeUsage } =
    useCheckFundingType()

  const hasNavigatedAfterSave = useRef(false)
  const isSaving = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  /**
   * Funding types already persisted on the care package. Only these can be
   * referenced by the rota, so a row the user has just added — or one with no
   * type picked yet — is removed without asking the API (CM-4434).
   */
  const persistedFundingTypes = new Set(
    (
      resident as unknown as {
        carePackage?: { fundingSources?: { type: string }[] }
      }
    )?.carePackage?.fundingSources?.map((source) => source.type) ?? []
  )

  /**
   * Navigate only once the form is no longer dirty. The unsaved-changes route
   * guard tears its blocker down in an effect keyed on the dirty state, and
   * child effects run before the parent's, so waiting here guarantees the
   * blocker is gone before we leave. Navigating straight from the update
   * callback raced that teardown and surfaced a "Changes not saved" prompt on
   * a save that had already succeeded (CM-4434).
   */
  useEffect(() => {
    if (hasSaved && !isDirty && !hasNavigatedAfterSave.current) {
      hasNavigatedAfterSave.current = true
      navigate(-1)
    }
  }, [hasSaved, isDirty, navigate])

  useEffect(() => {
    const baseSources = (
      resident as unknown as {
        carePackage?: { fundingSources?: { type: string }[] }
      }
    )?.carePackage?.fundingSources
    if (
      !chargeRatesData ||
      !baseSources?.length ||
      chargeRatesInitialized.current
    ) {
      return
    }
    chargeRatesInitialized.current = true
    const updatedFundingSources = baseSources.map(
      (source: { type: string }) => {
        const sourceRates = chargeRatesData
          .filter((cr) => cr.fundingTypeId === source.type)
          .map((cr) => ({
            visitTypeId: cr.eventTypeId,
            visitTypeName: cr.visitTypeName ?? '',
            amount: Number(cr.rate),
            unit: cr.rateType === 'HOURLY' ? 'per_hour' : 'fixed'
          }))
        return { ...source, chargeRates: sourceRates }
      }
    )
    form.setFieldsValue({ fundingSources: updatedFundingSources })
  }, [chargeRatesData, resident, form])

  const handleFormValuesChange = (values: ResidentCarePackageDTO) => {
    // Ignore changes that land while a save is in flight. Removing a funding
    // type resolves asynchronously, so its form update could arrive after
    // submit had already cleared the dirty flag, re-arming the route guard
    // against a save that succeeded (CM-4434). This covers the whole save,
    // including the charge rates request that precedes the resident update.
    if (values && !isSaving.current) {
      setIsDirty(true)
    }
  }

  const handleOnUpdateFinish = async (values: ResidentCarePackageDTO) => {
    // Guard against a second submit slipping in before the mutation reports
    // its loading state — repeated Save clicks fired duplicate requests and
    // duplicate Mixpanel events (CM-4434).
    if (isSaving.current) {
      return
    }
    isSaving.current = true
    setIsSubmitting(true)
    setIsDirty(false)
    // Clear any success latched by an earlier attempt, so the navigation effect
    // can only act on the save starting here.
    setHasSaved(false)
    if (showChargeRates && resident?.id) {
      const fundingSources =
        (values.fundingSources as FundingSourceWithChargeRates[]) ?? []
      try {
        await updateChargeRates({
          fundingSources: fundingSources.map((source) => ({
            type: source.type,
            chargeRates: source.chargeRates ?? []
          }))
        })
      } catch {
        isSaving.current = false
        setIsSubmitting(false)
        setIsDirty(true)
        return
      }
      const existingRateKeys = new Set(
        (chargeRatesData ?? []).map(
          (cr) => `${cr.fundingTypeId}:${cr.eventTypeId}`
        )
      )
      const newRatesCount = fundingSources.flatMap((source) =>
        (source.chargeRates ?? []).filter(
          (cr) => !existingRateKeys.has(`${source.type}:${cr.visitTypeId}`)
        )
      ).length
      if (newRatesCount > 0) {
        sendEvent(MixpanelEvents.CHARGE_RATE_ADDED, {
          ...(newRatesCount > 1 && { numberOfChargeRates: newRatesCount })
        })
      }
    }
    try {
      await updateResident({
        carePackage: {
          ...values,
          fundingSources: values?.fundingSources?.map((source) => ({
            type: source.type,
            hoursPerWeek: source.hoursPerWeek
          }))
        }
      } as unknown as ResidentBody)
    } catch {
      isSaving.current = false
      setIsSubmitting(false)
      setIsDirty(true)
    }
  }

  const initialValues: ResidentCarePackageDTO =
    residentWithPackages?.carePackage || {}

  // Test ID constants following best practices
  const baseTestId = 'care-package-edit'
  const cancelButtonTestId = commonPatterns.button(baseTestId, 'cancel')
  const saveButtonTestId = commonPatterns.button(baseTestId, 'save')
  const addFundingButtonTestId = commonPatterns.button(
    baseTestId,
    'add-funding'
  )
  const additionalNotesInputTestId = commonPatterns.input(
    baseTestId,
    'additional-notes'
  )
  const visitNotesInputTestId = commonPatterns.input(baseTestId, 'visit-notes')
  const mainSectionTestId = `${baseTestId}-main-section`
  const fundingFormCardTestId = `${baseTestId}-funding-form-card`
  const additionalNotesFormCardTestId = `${baseTestId}-additional-notes-form-card`
  const visitPreferencesFormCardTestId = `${baseTestId}-visit-preferences-form-card`
  const otherSectionTestId = `${baseTestId}-other-section`
  const navigationModalTestId = `${baseTestId}-navigation-modal`
  const deleteFundingModalTestId = `${baseTestId}-delete-funding-modal`

  return (
    <>
      <PageHeader
        title={`${t('EDIT')} ${t('CARE_PACKAGE_AND_PATTERNS')}`}
        sticky
      >
        <ButtonsContainer>
          <Button onClick={() => navigate(-1)} data-testid={cancelButtonTestId}>
            {t('CANCEL')}
          </Button>
          <Button
            variant="primary"
            onClick={form.submit}
            isLoading={
              isSubmitting || isLoadingUpdate || isCheckingFundingTypeUsage
            }
            disabled={isSubmitting || isCheckingFundingTypeUsage}
            data-testid={saveButtonTestId}
          >
            {t('SAVE')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper>
        <SectionHeader
          title={t('CARE_PACKAGE_AND_PATTERNS')}
          testId={mainSectionTestId}
        />
        <Form
          form={form}
          onFinish={handleOnUpdateFinish}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          onValuesChange={handleFormValuesChange}
          data-testid={`${baseTestId}-form`}
        >
          <AntdForm.List name="fundingSources">
            {(fields, { add, remove }) => (
              <FormCard
                title={t('FUNDING')}
                rightChildren={
                  <Button
                    variant="text"
                    type="button"
                    onClick={() => {
                      add()
                      scrollToSection(ref as unknown as RefObject<HTMLElement>)
                    }}
                    data-testid={addFundingButtonTestId}
                  >
                    <Add />
                    {t('ADD_FUNDING')}
                  </Button>
                }
                testId={fundingFormCardTestId}
              >
                {fields.map((field, index) => {
                  const fundingTypeId = form.getFieldValue([
                    'fundingSources',
                    field.name,
                    'type'
                  ])

                  const handleDelete = async () => {
                    if (
                      hasRota &&
                      flagsRosteringChangeManagementPart2Enabled &&
                      persistedFundingTypes.has(fundingTypeId)
                    ) {
                      let addressUsed: FundingTypeUsageCheck
                      try {
                        addressUsed = await checkFundingTypeUsedInRota({
                          fundingTypeId,
                          residentId: resident?.id || ''
                        })
                      } catch {
                        showToast({
                          title: t('SOMETHING_WENT_WRONG'),
                          type: 'error'
                        })
                        return
                      }

                      // A key the response omits means "no usage of this kind",
                      // so treat it as empty rather than as a blocker.
                      const areAllArraysEmpty =
                        !addressUsed?.instances?.length &&
                        !addressUsed?.patterns?.length &&
                        !addressUsed?.revisions?.length

                      if (areAllArraysEmpty) {
                        remove(index)
                      } else {
                        setShowDeleteFundingTypeModal(true)
                      }
                    } else {
                      remove(index)
                    }
                  }

                  return (
                    <div
                      key={field.name}
                      ref={ref as unknown as LegacyRef<HTMLDivElement>}
                    >
                      <FundingFormItem
                        key={field.name}
                        onDelete={handleDelete}
                        name={field?.name}
                        fundingTypeOptions={fundingTypeOptions}
                        selectedResident={resident?.id}
                        showChargeRates={!!showChargeRates}
                        visitTypes={visitTypes}
                        isAdmin={canManageChargeRates}
                        isDeleteDisabled={
                          isCheckingFundingTypeUsage || isSubmitting
                        }
                      />
                    </div>
                  )
                })}
              </FormCard>
            )}
          </AntdForm.List>
          <FormCard
            title={t('ADDITIONAL_NOTES')}
            testId={additionalNotesFormCardTestId}
          >
            <FormItem name="additionalNotes">
              <TextArea
                autoSize={{
                  minRows: 3
                }}
                maxLength={200}
                showCount
                data-testid={additionalNotesInputTestId}
              />
            </FormItem>
          </FormCard>
          {hasRota && (
            <>
              <SectionHeader title={t('OTHER')} testId={otherSectionTestId} />
              <FormCard
                title={t('VISIT_PREFERENCES')}
                subtitle={t('VISIT_PREFERENCES_DESC', {
                  nominative: residentNominative
                })}
                testId={visitPreferencesFormCardTestId}
              >
                <FormItem name="visitNotes">
                  <TextArea
                    autoSize={{
                      minRows: 3
                    }}
                    maxLength={200}
                    showCount
                    data-testid={visitNotesInputTestId}
                  />
                </FormItem>
              </FormCard>
            </>
          )}
        </Form>
      </PageWrapper>
      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('CARE_PACKAGE_NAVIGATION_MODAL_CONTENT')}
        data-testid={navigationModalTestId}
      />
      {showDeleteFundingTypeModal && (
        <DeleteFundingTypeModal
          showModal={showDeleteFundingTypeModal}
          setShowModal={setShowDeleteFundingTypeModal}
          data-testid={deleteFundingModalTestId}
        />
      )}
    </>
  )
}

export default EditCarePackageAndPatternsScreen
